/**
 * InboxEmailRow — dense single-line email row (Gmail-compact).
 * Unread dot · sender · subject — preview · signals · matter chip · time.
 * Used by search results and the non-conversation list mode. Hovering swaps
 * the time for row actions (star / archive / read / delete).
 */

import { useState } from 'react';
import { Star, Archive, EnvelopeSimple, Trash, Paperclip, Flag, LinkSimple } from '@phosphor-icons/react';
import type { EmailSummary } from '@/lib/inboxApi';
import { usePreferences } from '@/lib/preferences';

const FONT = "'IBM Plex Sans', sans-serif";

function senderDisplay(email: EmailSummary): string {
  const addr =
    email.direction === 'inbound'
      ? email.fromAddress
      : (email.toAddresses[0] ?? email.fromAddress);
  const m = addr.match(/^"?([^"<]+?)"?\s*<[^>]+>$/); // "Name <addr>" → Name
  if (m && m[1]) return m[1].trim();
  const local = (addr.split('@')[0] ?? addr).replace(/[._-]+/g, ' ').trim();
  return local.replace(/\b\w/g, (c) => c.toUpperCase()) || addr; // raj.vnerds → Raj Vnerds
}

function timeDisplay(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

interface Props {
  email: EmailSummary;
  isSelected: boolean;
  anySelected: boolean;
  onSelect: (id: string) => void;
  onStarToggle: (id: string) => void;
  onArchive: (id: string) => void;
  onMarkRead: (id: string, isRead: boolean) => void;
  onDelete: (id: string) => void;
  onClick: (id: string) => void;
}

export function InboxEmailRow({
  email, isSelected, anySelected,
  onSelect, onStarToggle, onArchive, onMarkRead, onDelete, onClick,
}: Props) {
  const [hovered, setHovered] = useState(false);
  const isUnread = !email.isRead;
  const showCheckbox = hovered || anySelected || isSelected;
  const name = senderDisplay(email);

  // ONE density preference for the whole application. The inbox used to carry its own
  // (`EmailSettings.density`) with the same two values and a separate control; nothing read
  // either of them, so two screens offered a choice that changed nothing and would have
  // disagreed the moment one started working.
  const compact = usePreferences().density === 'compact';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(email.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 12px', height: compact ? 26 : 32,
        borderBottom: '1px solid var(--border-subtle)',
        cursor: 'pointer', userSelect: 'none',
        background: isSelected ? 'var(--surface-selected)' : hovered ? 'var(--surface-hover)' : 'transparent',
        borderLeft: isSelected ? '2px solid #2563EB' : '2px solid transparent',
        transition: 'background 0.08s',
      }}
    >
      {/* Select ↔ unread dot */}
      <div
        onClick={(e) => { e.stopPropagation(); onSelect(email.id); }}
        style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {showCheckbox ? (
          <div style={{
            width: 15, height: 15, borderRadius: 4,
            border: isSelected ? '2px solid #2563EB' : '1.5px solid #cbd5e1',
            background: isSelected ? '#2563EB' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isSelected && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1.5 5.5L3.5 7.5L8.5 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        ) : (
          isUnread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563EB' }} />
        )}
      </div>

      {/* Sender (fixed) */}
      <span style={{
        width: 168, flexShrink: 0, fontFamily: FONT, fontSize: 13,
        fontWeight: isUnread ? 700 : 500, color: 'var(--text-primary)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {name}
      </span>

      {/* Subject — preview (single line, flexes) */}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 5, overflow: 'hidden' }}>
        <span style={{
          fontFamily: FONT, fontSize: 13, fontWeight: isUnread ? 600 : 400,
          color: isUnread ? 'var(--text-primary)' : 'var(--text-secondary)',
          flexShrink: 0, maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {email.subject || '(no subject)'}
        </span>
        {email.snippet && (
          <span style={{
            flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 12.5, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            — {email.snippet}
          </span>
        )}
      </span>

      {/* Right cluster: signals · matter · time / actions */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {email.attachmentCount > 0 && <Paperclip size={12} color="#9ca3af" />}
        {email.hasReplyFlag && <Flag size={11} color="#f59e0b" />}
        {email.linkStatus === 'confirmed' && <LinkSimple size={11} color="#059669" />}
        {email.matter && (
          <span style={{
            padding: '1px 6px', background: 'var(--surface-selected)', color: '#2563EB',
            borderRadius: 4, fontFamily: FONT, fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            {email.matter.caseNumber}
          </span>
        )}
        {hovered ? (
          <span style={{ display: 'flex', gap: 2 }}>
            <HoverBtn title="Star" onClick={(e) => { e.stopPropagation(); onStarToggle(email.id); }}>
              <Star size={14} weight={email.isStarred ? 'fill' : 'regular'} color={email.isStarred ? '#F6C244' : undefined} />
            </HoverBtn>
            <HoverBtn title="Archive" onClick={(e) => { e.stopPropagation(); onArchive(email.id); }}><Archive size={14} /></HoverBtn>
            <HoverBtn title={isUnread ? 'Mark read' : 'Mark unread'} onClick={(e) => { e.stopPropagation(); onMarkRead(email.id, isUnread); }}><EnvelopeSimple size={14} /></HoverBtn>
            <HoverBtn title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(email.id); }}><Trash size={14} /></HoverBtn>
          </span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {email.isStarred && <Star size={13} weight="fill" color="#F6C244" />}
            <span style={{
              width: 44, textAlign: 'right', fontFamily: FONT, fontSize: 11.5, whiteSpace: 'nowrap',
              color: 'var(--text-muted)', fontWeight: isUnread ? 600 : 400,
            }}>
              {timeDisplay(email.receivedAt ?? email.sentAt)}
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

// ─── Tiny hover icon button ──────────────────────────────────────

function HoverBtn({ title, onClick, children }: { title: string; onClick: (e: React.MouseEvent) => void; children: React.ReactNode }) {
  const [h, setH] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', borderRadius: 5, background: h ? 'var(--surface-sunken)' : 'transparent',
        color: 'var(--text-muted)', cursor: 'pointer', padding: 0, flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}
